import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Row, Col } from "react-bootstrap";
import { useState, useEffect } from "react";
import GenericPostItem from "./GenericPostItem";

/**
 * Post-only kanban board.
 * Fixed columns + fixed rules.
 * Only adapters vary between contexts.
 */
export default function GenericKanbanBoard({
  columns,
  setColumns,

  getId = (task) => task?.id ?? task?.post?.id,
  getPost = (task) => task?.post ?? task,
  ItemMenu,
  onEdit,
  onStatusChange, // async (post, targetColumnId) => void
  columnWidthPct = 20,
  maxTaskListWidthPx = 240,
  gapPx = 20,
  mobileBreakpointPx = 768, // <— add
  /** optional: extra props for one card, e.g. { readOnly, titlePrefix } */
  getItemProps = null,
}) {
  const safeId = (task) => String(getId(task));

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < mobileBreakpointPx
  );
  const columnIds = Object.keys(columns ?? {});
  const [activeColumn, setActiveColumn] = useState(columnIds[0] ?? "");

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < mobileBreakpointPx);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [mobileBreakpointPx]);

  // FIXED RULES (same everywhere)
  const isMoveBlocked = (sourceId, destId, destColName) => {
    if (sourceId === "planned" && destId !== "planned") {
      alert(
        "Cannot move posts out of Planned. Use the planning tool in Edit mode",
      );
      return true;
    }
    if (sourceId === "published" && destId !== "published") {
      alert(
        "Cannot move posts out of Published. Use the planning tool in Edit mode",
      );
      return true;
    }
    if (
      sourceId !== destId &&
      (destId === "planned" || destId === "published")
    ) {
      alert(
        `You cannot move posts into the "${destColName || destId}" column.`,
      );
      return true;
    }
    return false;
  };

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceCol = columns?.[source.droppableId];
    const destCol = columns?.[destination.droppableId];
    if (!sourceCol || !destCol) return;

    // ---- FIXED BLOCK RULES ----
    if (
      isMoveBlocked(source.droppableId, destination.droppableId, destCol?.name)
    ) {
      return; // snap-back
    }

    // ---- SAME COLUMN ----
    if (source.droppableId === destination.droppableId) {
      const tasks = [...(sourceCol.tasks ?? [])];
      const [movedTask] = tasks.splice(source.index, 1);
      tasks.splice(destination.index, 0, movedTask);

      setColumns((prev) => ({
        ...prev,
        [source.droppableId]: { ...prev[source.droppableId], tasks },
      }));

      // optional: if you treat reorder as a status change, call it
      // await onStatusChange?.(getPost(movedTask), destination.droppableId);

      return;
    }

    // ---- CROSS COLUMN ----
    const sourceTasks = [...(sourceCol.tasks ?? [])];
    const destTasks = [...(destCol.tasks ?? [])];

    const [movedTask] = sourceTasks.splice(source.index, 1);
    destTasks.splice(destination.index, 0, movedTask);

    // optimistic UI
    setColumns((prev) => ({
      ...prev,
      [source.droppableId]: { ...prev[source.droppableId], tasks: sourceTasks },
      [destination.droppableId]: {
        ...prev[destination.droppableId],
        tasks: destTasks,
      },
    }));

    // persist status (adapter)
    if (onStatusChange) {
      await onStatusChange(getPost(movedTask), destination.droppableId);
    }
  };

   return (
    <div className="task-board">
      {isMobile && (
        <select
          className="form-select mb-3"
          value={activeColumn}
          onChange={(e) => setActiveColumn(e.target.value)}
        >
          {Object.entries(columns ?? {}).map(([columnId, column]) => (
            <option key={columnId} value={columnId}>
              {column.name} ({(column.tasks ?? []).length})
            </option>
          ))}
        </select>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <div style={{ display: "flex", gap: `${gapPx}px` }}>
          <Row className="w-100 flex-fill">
            {Object.entries(columns ?? {}).filter(([columnId]) => !isMobile || columnId === activeColumn).map(([columnId, column]) => (
              <Droppable key={columnId} droppableId={columnId}>
                {(provided) => (
                  <Col
                    xs={12}
                    md
                    style={{
                      flex: isMobile ? "0 0 100%" : "1 1 0",
                      maxWidth: isMobile ? "100%" : "none",
                      minWidth: 0,
                    }}
                  >
                    <div className="task-list">
                      <div className="card bg-light shadow-none h-100">
                        <div className="card-header bg-transparent border-bottom d-flex align-items-start">
                          <div className="flex-grow-1">
                            <h4 className="card-title mb-0">
                              {column.name}{" "}
                              <span className="ml-5 text-muted">
                                ({(column.tasks ?? []).length})
                              </span>
                            </h4>
                          </div>
                        </div>

                        <div className="tasklist-content p-3 simplebar-scrollable-y">
                          <div className="simplebar-wrapper">
                            <div
                              className="tasks"
                              style={{ width: "100%" }}
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {(column.tasks ?? []).map((task, index) => (
                                <Draggable
                                  key={safeId(task)}
                                  draggableId={safeId(task)}
                                  index={index}
                                >
                                  {(dragProvided) => (
                                    <GenericPostItem
                                      post={getPost(task)}
                                      task={task}
                                      provided={dragProvided}
                                      ItemMenu={ItemMenu}
                                      onEdit={onEdit ? () => onEdit(getPost(task)) : undefined}
                                      {...(getItemProps ? getItemProps(getPost(task), task) : {})}
                                    />
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                )}
              </Droppable>
            ))}
          </Row>
        </div>
      </DragDropContext>
    </div>
  );
}
